/**
 * Calendar data fetching hook with flexible date ranges.
 *
 * Fetches all event-kind entities (meetings, reminders, custom event types)
 * in a single query. Reminder-specific features (subject navigation, type
 * filter) are handled via the CalendarEvent overlay in eventMapper.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useMyIntegrationEntityIds } from '@/hooks/integrations/useMyIntegrationEntityIds';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { usePermissions } from '@/hooks/usePermissions';
import { useReminderAutomationEnabled } from '@/hooks/useReminderAutomationEnabled';
import type {
  FilterCondition,
  FilterConditionGroup,
} from '@/lib/crm/filters/types';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import { CALENDAR_EVENT_ASSOCIATIONS } from '@/lib/meetings/calendarAssociations';
import { applyMineScopingNested } from '@/lib/meetings/mineScopingFilters';
import { calendarDateIn } from '@/lib/time';
import type { EntityTypeName, FilterGroup, PropertyFilter } from '@/types/crm';
import { convertConditionGroupsToFilterGroups } from '@/utils/filterUtils';

import {
  addCalendarDays,
  addCalendarMonths,
  calendarDayOf,
  calendarDayOfWeek,
  daysInCalendarMonth,
  startOfCalendarMonth,
  startOfCalendarWeek,
} from '../utils/calendarDays';
import type { CalendarEvent } from '../utils/eventMapper';
import {
  filterCalendarEntities,
  mapCrmObjectsToCalendarEvents,
  mapCrmTasksToCalendarEvents,
} from '../utils/eventMapper';
import { isReminderVisibleToUser } from '../utils/reminderUtils';

// ============================================================================
// Types
// ============================================================================

export interface ReminderEntityType {
  name: string;
  label: string;
}

export interface CalendarDataResult {
  events: CalendarEvent[];
  isLoading: boolean;
  /** True when showing cached data from a previous date range while new data loads */
  isPlaceholderData: boolean;
  error: unknown;
  /** Unique subject entity types present in the current reminders data. Used to
   * populate the filter sidebar checkboxes. */
  reminderEntityTypes: ReminderEntityType[];
}

// ============================================================================
// Date Range Helpers
// ============================================================================

/**
 * Get the first day of the week based on browser locale.
 * Returns 0 for Sunday, 1 for Monday, etc. (JS Date format).
 */
export function getWeekStartDay(): number {
  try {
    const locale = new Intl.Locale(navigator.language);
    // getWeekInfo() returns firstDay: 1=Monday...7=Sunday
    // Convert to JS Date format: 0=Sunday...6=Saturday
    const info = (
      locale as Intl.Locale & { getWeekInfo?: () => { firstDay?: number } }
    ).getWeekInfo?.();
    if (info?.firstDay) {
      return info.firstDay === 7 ? 0 : info.firstDay;
    }
  } catch {
    // Browser may not support getWeekInfo
  }
  return 0; // Default to Sunday for US
}

/**
 * Fetch-range helpers.
 *
 * Each returns the half-open instant range `[startDate, endDate)` the view
 * covers, with both boundaries at MIDNIGHT IN THE PROFILE TIMEZONE (see
 * `utils/calendarDays.ts`). That is the whole point of the timezone argument:
 * a browser-local boundary fetches the wrong 24 hours for anyone whose profile
 * timezone isn't their laptop's, and the grid then has nothing to render in
 * the cells it is about to draw.
 */

/**
 * Get the start and end of a day.
 */
export function getDayRange(
  date: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  const startDate = calendarDayOf(date, timezone);
  return { startDate, endDate: addCalendarDays(startDate, 1, timezone) };
}

/**
 * Get the start and end of a week containing the given date.
 */
export function getWeekRange(
  date: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  const startDate = startOfCalendarWeek(date, getWeekStartDay(), timezone);
  return { startDate, endDate: addCalendarDays(startDate, 7, timezone) };
}

/**
 * Get the start and end of a month containing the given date.
 */
export function getMonthRange(
  date: Date,
  timezone: string
): { startDate: Date; endDate: Date } {
  return {
    startDate: startOfCalendarMonth(date, timezone),
    endDate: addCalendarMonths(date, 1, timezone),
  };
}

/**
 * Get the range covering the full visible month-view GRID — including the
 * leading days from the previous month and the trailing days from the
 * next month that share weeks with the current month. The MonthView grid
 * always renders complete weeks, so events on those leading/trailing
 * days need to be fetched and shown alongside current-month events.
 *
 * Mirrors the grid-cell computation in `monthGridDays` (utils/calendarDays.ts),
 * which MonthView renders from — the two must cover the same span.
 */
export function getMonthGridRange(
  date: Date,
  timezone: string
): {
  startDate: Date;
  endDate: Date;
} {
  const weekStartDay = getWeekStartDay();
  const firstOfMonth = startOfCalendarMonth(date, timezone);

  // Days BEFORE the first of the month that share its starting week.
  const daysBeforeMonth =
    (calendarDayOfWeek(firstOfMonth, timezone) - weekStartDay + 7) % 7;
  const startDate = addCalendarDays(firstOfMonth, -daysBeforeMonth, timezone);

  // Total cells in the grid, rounded up to full weeks.
  const totalCells =
    Math.ceil((daysBeforeMonth + daysInCalendarMonth(date, timezone)) / 7) * 7;

  // Exclusive end (one day after the last visible cell).
  return {
    startDate,
    endDate: addCalendarDays(startDate, totalCells, timezone),
  };
}

// ============================================================================
// Local Date Helper
// ============================================================================

/**
 * Page size for the broad event-kind query. The select-step post-filter drops
 * cancelled and non-meeting entities client-side; pagination is widened so
 * those drops cannot push valid meetings off the visible page.
 */
const CALENDAR_PAGE_LIMIT = 500;

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to fetch calendar events for any date range.
 */
export function useCalendarData(
  startDate: Date,
  endDate: Date,
  orgId: number | undefined,
  options?: {
    search?: string;
    filterConditions?: FilterCondition[];
    /** When null, all reminders are shown. When a Set, only reminders whose
     * subjectEntityType is in the set are shown. Meetings are unaffected. */
    enabledReminderTypes?: Set<string> | null;
  }
): CalendarDataResult {
  // Get current user's entity ID to filter events in post-processing
  const { entityId, isLoading: isEntityLoading } = useCurrentUserEntity();

  // The date-only filters below (`ahoy_start_date` / `ahoy_end_date`) are
  // calendar days, so they must be the profile timezone's days — the same
  // zone the caller built `startDate`/`endDate` in. Read here rather than
  // taken as an argument so the two can never be threaded from different
  // sources; the range Dates and these strings then always name one day.
  const timezone = useDisplayTimezone();

  const {
    integrationEntityIds,
    isLoaded: areIntegrationsLoaded,
    error: integrationError,
  } = useMyIntegrationEntityIds(orgId);

  useEffect(() => {
    if (integrationError) {
      logger.error(
        'Failed to load integration entity IDs',
        integrationError as Error,
        { orgId }
      );
      logger.warn(
        'Integration lookup failed, falling back to attendee-only meetings',
        { orgId }
      );
    }
  }, [integrationError, orgId]);

  // Build 8 base date-overlap groups (Cases 1-8 below), then collapse them
  // with the 3 positive scoping arms from buildMineScopingArms (integration,
  // attendee, reminder-owner — see includeReminders below) into a single
  // nested group: `(date1 OR … OR date8) AND (arm1 OR arm2 OR arm3) AND
  // advancedFilters`. The arms are load-bearing on their own, not redundant
  // with each other: a solo calendar block (focus time, lunch) has no
  // attendees, so it carries no `ahoy_meeting_to_user` edge and is only
  // reachable via the integration arm; a meeting synced by a colleague's
  // calendar carries their integration, not the viewer's, so it's only
  // reachable via the attendee arm. Cancelled-meeting drop and non-meeting
  // passthrough still happen in select via filterCalendarEntities.
  // CALENDAR_PAGE_LIMIT remains high so the cancelled-meeting drop does not
  // push valid meetings off the page in dense calendars.
  const filterGroups: FilterGroup[] = useMemo(() => {
    const rangeStartIso = startDate.toISOString();
    const rangeEndIso = endDate.toISOString();
    const rangeStartDate = calendarDateIn(startDate, timezone);
    const rangeEndDate = calendarDateIn(endDate, timezone);

    const advancedFilterGroup: FilterConditionGroup = {
      id: 'calendar-advanced-filters',
      conditions: options?.filterConditions ?? [],
    };
    const advancedFilters = convertConditionGroupsToFilterGroups([
      advancedFilterGroup,
    ]).filter_groups.flatMap(g => g.filters);

    // Pure date groups (without advancedFilters), collapsed into a single
    // nested group via applyMineScopingNested — 1 filter group instead of the
    // pre-#13229 24-group cross-product.
    const pureDateGroups: FilterGroup[] = [
      // Group 1: timed events overlapping the range (Cases 1, 2)
      {
        filters: [
          {
            property: 'ahoy_end_time',
            operator: 'gt',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_start_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      // Group 2: all-day events with both start and end dates (Case 3) overlapping the window
      {
        filters: [
          {
            property: 'ahoy_end_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 3: hybrid — timed start, all-day end (Case 2)
      {
        filters: [
          {
            property: 'ahoy_end_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_start_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      // Group 4: hybrid — all-day start, timed end (Case 4)
      {
        filters: [
          {
            property: 'ahoy_end_time',
            operator: 'gt',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 5: start-only timed events (Case 5)
      {
        filters: [
          {
            property: 'ahoy_start_time',
            operator: 'gte',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_start_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      // Group 6: start-only all-day events (Case 7) — includes reminders
      {
        filters: [
          {
            property: 'ahoy_start_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_start_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 7: end-only all-day events (Case 8)
      {
        filters: [
          {
            property: 'ahoy_end_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_end_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
      // Group 8: end-only timed events (Case 6)
      {
        filters: [
          {
            property: 'ahoy_end_time',
            operator: 'gte',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_end_time',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
    ];
    // Scope to meetings that are mine via the shared positive scoping arms.
    // The arms replaced a single `ahoy_meeting_to_integration in_or_isnull
    // [...]` filter, whose absence arm meant "no integration edge I can SEE"
    // once association-target gating landed — so every colleague's org-visible
    // meeting matched it (#13192; arms stay positive `in`, never an absence
    // test — see mineScopingFilters.ts). includeReminders: this query is
    // entity_kind 'event', which merges reminders in alongside meetings. A
    // reminder carries neither meeting association, so without its own arm it
    // would match nothing and vanish from the calendar.
    return applyMineScopingNested(
      pureDateGroups,
      integrationEntityIds,
      entityId,
      advancedFilters,
      { includeReminders: true }
    );
  }, [
    startDate,
    endDate,
    timezone,
    options?.filterConditions,
    integrationEntityIds,
    entityId,
  ]);

  const calendarQuery = useQuery({
    ...crmObjectListSentinelQuery(
      orgId ?? 0,
      { entity_kind: 'event' },
      {
        filter_groups: filterGroups,
        sort: 'ahoy_start_time',
        order: 'asc',
        search: options?.search || undefined,
        include_total_count: false,
        limit: CALENDAR_PAGE_LIMIT,
      },
      // Projection, not `include_associated_objects: true` — see
      // lib/meetings/calendarAssociations.ts. All-types made the backend gate and
      // hydrate every association type in the org on every calendar load.
      { include_associations: CALENDAR_EVENT_ASSOCIATIONS }
    ),
    enabled: !!orgId && !!entityId && areIntegrationsLoaded,
    staleTime: 60_000, // 1 minute
    gcTime: 5 * 60_000, // 5 minutes
    // Keep previous date range's events visible while new data loads
    placeholderData: keepPreviousData,
  });
  const list = useStoreBackedList(calendarQuery);

  const { hasPermission } = usePermissions();
  const canReadTasks = hasPermission('ahoy_task');

  // Tasks are not `event`-kind and cannot be made so, so they need their own
  // query merged into the same list. Query both ahoy_datetime (timed) and
  // ahoy_date (date-only) since ahoy_date may or may not be kept in sync with
  // ahoy_datetime depending on cascade wiring.
  const taskFilterGroups: FilterGroup[] = useMemo(() => {
    const rangeStartIso = startDate.toISOString();
    const rangeEndIso = endDate.toISOString();
    const rangeStartDate = calendarDateIn(startDate, timezone);
    const rangeEndDate = calendarDateIn(endDate, timezone);

    return [
      {
        filters: [
          {
            property: 'ahoy_datetime',
            operator: 'gte',
            value: rangeStartIso,
          } as PropertyFilter,
          {
            property: 'ahoy_datetime',
            operator: 'lt',
            value: rangeEndIso,
          } as PropertyFilter,
        ],
      },
      {
        filters: [
          {
            property: 'ahoy_date',
            operator: 'gte',
            value: rangeStartDate,
          } as PropertyFilter,
          {
            property: 'ahoy_date',
            operator: 'lt',
            value: rangeEndDate,
          } as PropertyFilter,
        ],
      },
    ];
  }, [startDate, endDate, timezone]);

  const tasksQuery = useQuery({
    ...crmObjectListSentinelQuery(
      orgId ?? 0,
      'ahoy_task' as EntityTypeName,
      {
        filter_groups: taskFilterGroups,
        sort: 'ahoy_datetime',
        order: 'asc',
        include_total_count: false,
        limit: CALENDAR_PAGE_LIMIT,
      },
      {}
    ),
    enabled: !!orgId && canReadTasks,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  const taskList = useStoreBackedList(tasksQuery);

  const remindersEnabled = useReminderAutomationEnabled(orgId);

  const objectTypesQuery = useObjectTypes(orgId);

  const entityTypeLabelLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of objectTypesQuery.data?.allObjectTypes ?? []) {
      map.set(t.entity_type_name, t.label);
    }
    return (entityTypeName: string) => map.get(entityTypeName);
  }, [objectTypesQuery.data?.allObjectTypes]);

  // Extract unique subject entity types from reminders for the filter UI.
  // Hidden when the automation is disabled.
  const reminderEntityTypes = useMemo((): ReminderEntityType[] => {
    if (!remindersEnabled) {
      return [];
    }
    const types = new Map<string, string>();
    for (const obj of list.results) {
      if (obj.entity_type_name !== 'ahoy_reminder') {
        continue;
      }
      // Only surface filter options for reminders the user can see (mirrors the
      // ownership filter applied to the rendered events).
      if (!isReminderVisibleToUser(obj, entityId)) {
        continue;
      }
      const assoc = obj.associations?.find(
        a => a.association_type_name === 'ahoy_reminder_to_subject'
      );
      const subject = assoc
        ? list.associatedObjects[assoc.target_entity_id]
        : undefined;
      if (subject?.entity_type_name) {
        types.set(
          subject.entity_type_name,
          entityTypeLabelLookup(subject.entity_type_name) ??
            subject.entity_type_name
        );
      }
    }
    return Array.from(types.entries()).map(([name, label]) => ({
      name,
      label,
    }));
  }, [
    list.results,
    list.associatedObjects,
    remindersEnabled,
    entityTypeLabelLookup,
    entityId,
  ]);

  const events = useMemo(() => {
    // Filter out reminders entirely when the automation is disabled; otherwise
    // keep only reminders owned by the current user or unowned (declutter — see
    // isReminderVisibleToUser). Non-reminder events always pass through.
    const results = remindersEnabled
      ? list.results.filter(
          obj =>
            obj.entity_type_name !== 'ahoy_reminder' ||
            isReminderVisibleToUser(obj, entityId)
        )
      : list.results.filter(obj => obj.entity_type_name !== 'ahoy_reminder');

    // Map all event-kind entities through the unified mapper.
    const allEvents = mapCrmObjectsToCalendarEvents(
      filterCalendarEntities(results),
      list.associatedObjects
    );

    // Apply the reminder type filter — when enabledReminderTypes is a Set,
    // drop reminders whose subject entity type is not in the set.
    const enabledReminderTypes = options?.enabledReminderTypes;
    const allEventsAfterReminderFilter =
      enabledReminderTypes == null
        ? allEvents
        : allEvents.filter(
            e =>
              e.entity_type_name !== 'ahoy_reminder' ||
              enabledReminderTypes.has(e.subjectEntityType ?? '')
          );

    const taskEvents = canReadTasks
      ? mapCrmTasksToCalendarEvents(taskList.results)
      : [];

    return [...allEventsAfterReminderFilter, ...taskEvents];
  }, [
    list.results,
    list.associatedObjects,
    remindersEnabled,
    options?.enabledReminderTypes,
    entityId,
    taskList.results,
    canReadTasks,
  ]);

  return {
    events,
    // Treat "integrations still loading" as loading too. The meetings query is
    // gated on `areIntegrationsLoaded`, so without this the hook would briefly
    // report ready (isLoading=false, events=[]) before the first fetch even
    // starts, letting the calendar flash an empty/ready UI.
    isLoading:
      list.isLoading ||
      isEntityLoading ||
      !areIntegrationsLoaded ||
      (canReadTasks && taskList.isLoading),
    isPlaceholderData: list.isPlaceholderData,
    error: list.error ?? null,
    reminderEntityTypes,
  };
}
